import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MenuBook as MenuBookIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';

// =============================================================================
// Contenuto del manuale. Ogni voce è coerente con ciò che il ruolo può davvero
// fare nell'app (menu, permessi backend, flusso sul campo). Niente di inventato.
// =============================================================================

const ETICHETTA_RUOLO = {
  admin: 'Amministratore di sistema',
  amministratore: 'CapoCommessa',
  capocantiere: 'Capocantiere',
  tecnico: 'Tecnico',
};

// --- Sezioni "ufficio", condivise da Amministratore di sistema e CapoCommessa ---
const SEZIONI_UFFICIO = [
  {
    title: 'La tua schermata iniziale: la Dashboard',
    blocks: [
      { kind: 'text', value: 'Quando accedi atterri sulla Dashboard, una panoramica con le statistiche dell’attività: numero di clienti, cantieri attivi, interventi di oggi e del mese, stato e tipologia degli interventi, avanzamento delle lavorazioni (task) e i prossimi interventi in programma.' },
    ],
  },
  {
    title: 'Clienti',
    blocks: [
      { kind: 'text', value: 'Dalla voce di menu "Clienti" gestisci l’anagrafica dei clienti.' },
      { kind: 'bullets', value: [
        'Crea, modifica ed elimina un cliente (ragione sociale, partita IVA, indirizzo, referente e recapiti).',
        'Apri un cliente per vederne la scheda e i rapportini collegati.',
      ] },
    ],
  },
  {
    title: 'Cantieri',
    blocks: [
      { kind: 'text', value: 'Dalla voce "Cantieri" gestisci i cantieri, ciascuno collegato a un cliente.' },
      { kind: 'bullets', value: [
        'Crea, modifica ed elimina un cantiere (con indirizzo e posizione, usata per il controllo GPS delle timbrature).',
        'Importa l’elenco delle lavorazioni (task) del cantiere da file, e gestiscile.',
      ] },
    ],
  },
  {
    title: 'Interventi',
    blocks: [
      { kind: 'text', value: 'È il cuore della pianificazione. Dalla voce "Interventi" vedi tutti gli interventi e ne crei di nuovi.' },
      { kind: 'steps', value: [
        'Crea un intervento scegliendo cliente, cantiere, data e orari.',
        'Indica la tipologia: Manutenzione o Impianto.',
        'Assegna il capocantiere e i tecnici che vi parteciperanno.',
        'Se serve, imposta interventi ricorrenti.',
      ] },
      { kind: 'text', value: 'Puoi modificare o eliminare un intervento, ordinare e cercare nell’elenco, e aprirlo per vederne il dettaglio. L’assegnazione del capocantiere è ciò che determina chi gestirà presenze, timbrature e rapportino sul campo.' },
    ],
  },
  {
    title: 'Calendario',
    blocks: [
      { kind: 'text', value: 'Il Calendario mostra gli interventi come eventi, colorati per tipologia (con una legenda dei colori in alto). Clicca un intervento per aprirne il dettaglio.' },
    ],
  },
  {
    title: 'Veicoli',
    blocks: [
      { kind: 'text', value: 'Dalla voce "Veicoli" gestisci l’anagrafica dei mezzi: creazione, modifica ed eliminazione, e ne consulti la disponibilità.' },
    ],
  },
  {
    title: 'Rapportini',
    blocks: [
      { kind: 'text', value: 'Dalla voce "Rapportini" vedi tutti i rapportini di lavoro.' },
      { kind: 'bullets', value: [
        'Due schede: "Compilati" e "Da compilare" (gli interventi conclusi che non hanno ancora un rapportino).',
        'Filtri per data, per cliente e per utente (capocantiere/tecnico): il filtro per utente considera tutti i partecipanti, non solo chi ha compilato.',
        'Puoi compilare un rapportino mancante e raccogliere la firma del cliente.',
      ] },
    ],
  },
  {
    title: 'Presenze e timbrature',
    blocks: [
      { kind: 'text', value: 'Di norma presenze e timbrature le registra il capocantiere sul campo. Come CapoCommessa puoi comunque gestirle per qualunque intervento, aprendolo e usando il pulsante "Timbrature".' },
    ],
  },
];

const SEZIONE_UTENTI = {
  title: 'Gestione Utenti (riservata all’Amministratore di sistema)',
  blocks: [
    { kind: 'text', value: 'Dalla voce "Utenti" gestisci le persone che usano l’app.' },
    { kind: 'bullets', value: [
      'Crea un nuovo utente con nome, email, nome utente, password e ruolo.',
      'Modifica o elimina un utente.',
      'I ruoli assegnabili sono: CapoCommessa, Capocantiere e Tecnico.',
      'A un Capocantiere puoi attivare il permesso "Può creare interventi": gli fa comparire il pulsante "Nuovo Intervento". Modifica ed eliminazione degli interventi restano comunque al CapoCommessa.',
    ] },
    { kind: 'note', value: 'Questa è l’unica funzione riservata all’Amministratore di sistema: i CapoCommessa non vedono la voce Utenti e non possono gestire gli account.' },
  ],
};

// --- Sezioni comuni a tutti i ruoli (in fondo) ---
const SEZIONI_COMUNI = [
  {
    title: 'Accedere e uscire',
    blocks: [
      { kind: 'bullets', value: [
        'Entra inserendo il tuo nome utente e la tua password.',
        'Per uscire, clicca sul tuo nome (in basso a sinistra sul computer, in alto sul telefono) e scegli "Esci".',
      ] },
    ],
  },
  {
    title: 'Muoversi nell’app',
    blocks: [
      { kind: 'text', value: 'Le funzioni si raggiungono dal menu laterale (a sinistra sul computer; sul telefono si apre con il pulsante ☰ in alto). Le voci che vedi nel menu dipendono dal tuo ruolo: questo manuale descrive solo ciò che è disponibile per te.' },
    ],
  },
  {
    title: 'Uso offline (senza connessione)',
    blocks: [
      { kind: 'text', value: 'L’app è utilizzabile anche dove non c’è campo. Può essere installata sul telefono come un’applicazione.' },
      { kind: 'bullets', value: [
        'I dati già consultati restano visibili anche senza rete.',
        'Le operazioni fatte offline (presenze, timbrature, rapportini) vengono salvate sul dispositivo: compare l’avviso "salvato offline".',
        'Appena torni online vengono inviate automaticamente, con l’orario reale in cui le hai registrate.',
      ] },
      { kind: 'note', value: 'Se il dispositivo ha la memoria piena (può capitare con rapportini ricchi di foto) l’app te lo segnala chiaramente, così sai che quel salvataggio va ripetuto quando torni online.' },
    ],
  },
];

// --- Manuali per ruolo ---
const INTRO = {
  admin: 'Hai accesso completo a tutta l’applicazione: anagrafiche, pianificazione, rapportini e — funzione esclusiva del tuo ruolo — la gestione degli utenti.',
  amministratore: 'Gestisci l’anagrafica e la pianificazione del lavoro: clienti, cantieri, interventi, veicoli e rapportini. (La gestione degli utenti è riservata all’Amministratore di sistema.)',
  capocantiere: 'Sei il responsabile sul campo: per i tuoi interventi registri le presenze del team, le timbrature della giornata e compili i rapportini. Vedi soltanto gli interventi in cui sei il capocantiere assegnato.',
  tecnico: 'Consulti il calendario e gli interventi a cui sei assegnato. Presenze, timbrature e rapportini sono gestiti dal capocantiere dell’intervento.',
};

const SEZIONI_CAPOCANTIERE = [
  {
    title: 'Dove trovi il tuo lavoro',
    blocks: [
      { kind: 'text', value: 'Quando accedi atterri sul Calendario. Dal Calendario o dalla voce "Interventi" trovi i tuoi interventi (vedi solo quelli in cui sei il capocantiere). Apri un intervento per vederne i dettagli: cliente, cantiere, data e orari, tecnici assegnati e lavorazioni previste.' },
      { kind: 'note', value: 'Se l’Amministratore di sistema ti ha attivato il permesso "Può creare interventi", nella pagina "Interventi" compare il pulsante "Nuovo Intervento": puoi pianificare tu un intervento. Di norma lascia te stesso come capocantiere; se lo assegni a un collega sarà lui a gestirlo e non lo vedrai più nella tua lista. Per modificarlo o eliminarlo, invece, devi passare dall’ufficio.' },
    ],
  },
  {
    title: '1) Registra le presenze (prima di tutto)',
    blocks: [
      { kind: 'text', value: 'Apri l’intervento e premi "Timbrature". Da lì registra chi è presente nel team: tu e i tecnici assegnati, segnando eventuali assenti e il motivo.' },
      { kind: 'note', value: 'Le presenze vanno registrate prima di poter timbrare: è il primo passo obbligatorio della giornata.' },
    ],
  },
  {
    title: '2) Timbrature della giornata',
    blocks: [
      { kind: 'text', value: 'Sempre dalla schermata "Timbrature", registri la sequenza della giornata, valida per tutto il team presente:' },
      { kind: 'steps', value: [
        'Partenza dalla Sede',
        'Arrivo Cantiere / Inizio Lavoro',
        'Inizio Pausa e Fine Pausa',
        'Fine Lavoro',
        'Partenza dal Cantiere',
        'Arrivo in Sede',
      ] },
      { kind: 'bullets', value: [
        'Alcune timbrature (arrivo in cantiere, fine lavoro, partenza dal cantiere) usano il GPS: se risulti lontano dal cantiere compare un avviso, ma non blocca la timbratura.',
        'A seconda della configurazione del cantiere, le timbrature di viaggio (partenza/arrivo sede) possono non essere richieste.',
      ] },
    ],
  },
  {
    title: '3) Compila il rapportino',
    blocks: [
      { kind: 'text', value: 'Dopo la timbratura di "Fine Lavoro" l’app ti propone il rapportino; in alternativa puoi usare il pulsante "Compila Ora" sull’intervento. Nel rapportino inserisci:' },
      { kind: 'bullets', value: [
        'Descrizione del lavoro svolto e materiali utilizzati.',
        'Le ore lavorate per ciascun tecnico.',
        'Le lavorazioni (task) completate e gli allegati, ad esempio foto (fino a 5 MB ciascuna).',
      ] },
      { kind: 'text', value: 'Per gli interventi di Manutenzione raccogli la firma del cliente direttamente sullo schermo: oltre alla firma è obbligatorio indicare il nome di chi firma.' },
      { kind: 'note', value: 'Se provi a salvare la firma senza aver inserito il nome del firmatario (o senza aver disegnato la firma), l’app te lo segnala con un messaggio: non resta più bloccata senza spiegazioni.' },
    ],
  },
  {
    title: 'I tuoi rapportini',
    blocks: [
      { kind: 'text', value: 'Dalla voce "Rapportini" trovi i tuoi rapportini, con le schede "Compilati" e "Da compilare". Da qui puoi compilare quelli mancanti e aggiungere la firma del cliente se manca.' },
    ],
  },
  {
    title: 'Lavorare senza connessione',
    blocks: [
      { kind: 'text', value: 'Tutto il flusso (presenze, timbrature, rapportino) funziona anche offline: i dati si salvano sul telefono e vengono inviati da soli quando torni online, mantenendo l’orario reale in cui hai timbrato. Vedi anche la sezione "Uso offline" più sotto.' },
    ],
  },
];

const SEZIONI_TECNICO = [
  {
    title: 'La tua schermata iniziale: il Calendario',
    blocks: [
      { kind: 'text', value: 'Quando accedi atterri sul Calendario, dove vedi gli interventi a cui partecipi, colorati per tipologia.' },
    ],
  },
  {
    title: 'I tuoi interventi',
    blocks: [
      { kind: 'text', value: 'Dalla voce "Interventi" (o cliccando un evento nel Calendario) apri un intervento a cui sei assegnato e ne consulti i dettagli: cliente, cantiere, data e orari, capocantiere, tecnici e lavorazioni previste.' },
      { kind: 'note', value: 'Vedi soltanto gli interventi a cui sei stato assegnato.' },
    ],
  },
  {
    title: 'Presenze, timbrature e rapportini',
    blocks: [
      { kind: 'text', value: 'Queste operazioni sono gestite dal capocantiere dell’intervento. Le tue ore di lavoro vengono registrate dal capocantiere all’interno del rapportino.' },
    ],
  },
];

const MANUALI = {
  admin: [...SEZIONI_UFFICIO, SEZIONE_UTENTI],
  amministratore: [...SEZIONI_UFFICIO],
  capocantiere: SEZIONI_CAPOCANTIERE,
  tecnico: SEZIONI_TECNICO,
};

// --- Rendering di un blocco di contenuto ---
const Blocco = ({ blocco }) => {
  if (blocco.kind === 'text') {
    return (
      <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', lineHeight: 1.75 }}>
        {blocco.value}
      </Typography>
    );
  }
  if (blocco.kind === 'bullets' || blocco.kind === 'steps') {
    const ordered = blocco.kind === 'steps';
    return (
      <Box component={ordered ? 'ol' : 'ul'} sx={{ pl: 3, mt: 0, mb: 1.5 }}>
        {blocco.value.map((t, i) => (
          <Box component="li" key={i} sx={{ mb: 0.75 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              {t}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  if (blocco.kind === 'note') {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1.25,
          alignItems: 'flex-start',
          bgcolor: '#fff8e1',
          border: '1px solid #ffe082',
          borderRadius: 2,
          p: 1.5,
          mb: 1.5,
        }}
      >
        <InfoIcon sx={{ color: '#f59e0b', fontSize: 20, mt: '2px' }} />
        <Typography variant="body2" sx={{ color: '#7c5e10', lineHeight: 1.6 }}>
          {blocco.value}
        </Typography>
      </Box>
    );
  }
  return null;
};

const Manuale = () => {
  const { user } = useSelector((state) => state.auth);
  const ruolo = user?.role === 'super_admin' ? 'admin' : (user?.role || 'tecnico');
  const sezioniRuolo = MANUALI[ruolo] || MANUALI.tecnico;
  const sezioni = [...sezioniRuolo, ...SEZIONI_COMUNI];
  const etichetta = ETICHETTA_RUOLO[ruolo] || ruolo;

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="md">
        {/* Intestazione */}
        <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#e50000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MenuBookIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                Manuale Utente
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestione Cantieri — NC Elettrica
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">La guida per il tuo ruolo:</Typography>
            <Chip label={etichetta} size="small" sx={{ bgcolor: '#e50000', color: 'white', fontWeight: 700 }} />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.75 }}>
            {INTRO[ruolo] || INTRO.tecnico}
          </Typography>
        </Paper>

        {/* Sezioni */}
        {sezioni.map((sezione, idx) => (
          <Accordion
            key={sezione.title}
            defaultExpanded={idx === 0}
            disableGutters
            elevation={0}
            sx={{
              mb: 1.5,
              borderRadius: '12px !important',
              border: '1px solid',
              borderColor: 'divider',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: { xs: 2, md: 3 }, '& .MuiAccordionSummary-content': { my: 1.5 } }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{sezione.title}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pt: 0, pb: 2.5 }}>
              {sezione.blocks.map((blocco, i) => (
                <Blocco key={i} blocco={blocco} />
              ))}
            </AccordionDetails>
          </Accordion>
        ))}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
          Per problemi o richieste contatta l’amministratore del sistema.
        </Typography>
      </Container>
    </Box>
  );
};

export default Manuale;
